import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import UserManagementModal from './UserManagementModal';

interface LeagueSettingsProps {
  leagueId: number;
  isManager: boolean;
  leagueHasStarted?: boolean;
  /** When set, show dynamic scoring toggle (Pick'em IPL-style points table). */
  dynamicScoringEnabled?: boolean;
  /** When set with dynamicScoringEnabled, show match-start picks summary email toggle (Pick'em). */
  matchStartPickSummaryEmail?: boolean;
  onMatchStartPickSummaryChange?: () => void;
  /** When set, show “all picks always visible” toggle (Pick'em; league creator only on backend). */
  pickemAllPicksAlwaysVisible?: boolean;
  onPickemAllPicksVisibilityChange?: () => void;
  onDynamicScoringChange?: () => void;
  onSettingsUpdated?: () => void;
  onLeaveLeague?: () => void;
  onLeagueDeleted?: () => void;
}

interface Settings {
  // Core Points (Always Enabled)
  correct_pick_points: string;
  
  // Optional Point Systems
  wrong_pick_enabled: string;
  wrong_pick_points: string;
  
  // Streak Penalties (Individual Controls)
  wrong_5_picks_streak_enabled: string;
  wrong_5_picks_streak_penalty: string;
  wrong_10_picks_streak_enabled: string;
  wrong_10_picks_streak_penalty: string;
  
  // Streak Bonuses (Individual Controls)
  correct_5_picks_streak_enabled: string;
  correct_5_picks_streak_bonus: string;
  correct_10_picks_streak_enabled: string;
  correct_10_picks_streak_bonus: string;
  
  // Total Runs Prediction (Individual Controls)
  total_runs_exact_prediction_enabled: string;
  total_runs_exact_prediction_points: string;
  total_runs_close_prediction_enabled: string;
  total_runs_close_prediction_points: string;
  total_runs_medium_prediction_enabled: string;
  total_runs_medium_prediction_points: string;
  
  man_of_match_prediction_enabled: string;
  man_of_match_prediction_points: string;
  
  // Win Margin Prediction
  win_margin_prediction_enabled: string;
  win_margin_prediction_points: string;
}

const LeagueSettings: React.FC<LeagueSettingsProps> = ({
  leagueId,
  isManager,
  leagueHasStarted = false,
  dynamicScoringEnabled,
  matchStartPickSummaryEmail,
  onMatchStartPickSummaryChange,
  pickemAllPicksAlwaysVisible,
  onPickemAllPicksVisibilityChange,
  onDynamicScoringChange,
  onSettingsUpdated,
  onLeaveLeague,
  onLeagueDeleted
}) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [dynamicOn, setDynamicOn] = useState(!!dynamicScoringEnabled);
  const [savingDynamic, setSavingDynamic] = useState(false);
  const [pickSummaryEmailOn, setPickSummaryEmailOn] = useState(!!matchStartPickSummaryEmail);
  const [savingPickSummaryEmail, setSavingPickSummaryEmail] = useState(false);
  const [allPicksVisibleOn, setAllPicksVisibleOn] = useState(!!pickemAllPicksAlwaysVisible);
  const [savingAllPicksVisible, setSavingAllPicksVisible] = useState(false);

  React.useEffect(() => {
    setDynamicOn(!!dynamicScoringEnabled);
  }, [dynamicScoringEnabled]);

  React.useEffect(() => {
    setPickSummaryEmailOn(!!matchStartPickSummaryEmail);
  }, [matchStartPickSummaryEmail]);

  React.useEffect(() => {
    setAllPicksVisibleOn(!!pickemAllPicksAlwaysVisible);
  }, [pickemAllPicksAlwaysVisible]);

  useEffect(() => {
    loadSettings();
  }, [leagueId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.getLeagueSettings(leagueId);
      
      // Merge with defaults to ensure all fields are present
      const defaultSettings = {
        correct_pick_points: '100',
        wrong_pick_enabled: 'false',
        wrong_pick_points: '-10',
        wrong_5_picks_streak_enabled: 'false',
        wrong_5_picks_streak_penalty: '-10',
        wrong_10_picks_streak_enabled: 'false',
        wrong_10_picks_streak_penalty: '-25',
        correct_5_picks_streak_enabled: 'false',
        correct_5_picks_streak_bonus: '10',
        correct_10_picks_streak_enabled: 'false',
        correct_10_picks_streak_bonus: '25',
        total_runs_exact_prediction_enabled: 'false',
        total_runs_exact_prediction_points: '50',
        total_runs_close_prediction_enabled: 'false',
        total_runs_close_prediction_points: '20',
        total_runs_medium_prediction_enabled: 'false',
        total_runs_medium_prediction_points: '10',
        man_of_match_prediction_enabled: 'false',
        man_of_match_prediction_points: '50',
        win_margin_prediction_enabled: 'false',
        win_margin_prediction_points: '25'
      };
      
      // Force update the core values to new defaults if they're old values
      const serverSettings = response.settings || {};
      const updatedSettings = { ...defaultSettings, ...serverSettings };
      
      // Force update all streak penalty and bonus values to new defaults
      // This ensures the new default values are always used
      updatedSettings.wrong_5_picks_streak_penalty = '-10';
      updatedSettings.wrong_10_picks_streak_penalty = '-25';
      updatedSettings.correct_5_picks_streak_bonus = '10';
      updatedSettings.correct_10_picks_streak_bonus = '25';
      
      // Override old values with new defaults
      if (serverSettings.correct_pick_points === '10' || !serverSettings.correct_pick_points) {
        updatedSettings.correct_pick_points = '100';
      }
      if (serverSettings.wrong_pick_points === '0' || !serverSettings.wrong_pick_points) {
        updatedSettings.wrong_pick_points = '-10';
      }
      
      console.log('Server settings:', serverSettings);
      console.log('Updated settings:', updatedSettings);
      
      setSettings(updatedSettings);
      
      // Only save the updated settings if user is a manager
      if (isManager) {
        try {
          await apiService.updateLeagueSettings(leagueId, updatedSettings);
          console.log('Updated league settings with new defaults');
        } catch (updateError) {
          console.error('Failed to update settings with new defaults:', updateError);
        }
      } else {
        console.log('User is not a manager, skipping automatic settings update');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load league settings');
      // Set default settings if loading fails
      resetToDefaults();
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key: keyof Settings, value: string) => {
    if (!settings) return;
    setSettings({
      ...settings,
      [key]: value
    });
  };

  const handleSave = async () => {
    if (!settings) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await apiService.updateLeagueSettings(leagueId, settings);
      
      setSuccess('League settings updated successfully!');
      onSettingsUpdated?.();
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to update league settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculatePoints = async () => {
    if (!isManager) {
      setError('Only league managers can recalculate points');
      return;
    }
    
    try {
      setRecalculating(true);
      setError(null);
      setSuccess(null);
      
      await apiService.recalculateLeaguePoints(leagueId);
      
      setSuccess('League points recalculated successfully!');
      onSettingsUpdated?.();
    } catch (error) {
      console.error('Error recalculating points:', error);
      setError('Failed to recalculate league points');
    } finally {
      setRecalculating(false);
    }
  };

  const resetToDefaults = () => {
    const defaultSettings: Settings = {
      correct_pick_points: '100',
      wrong_pick_enabled: 'false',
      wrong_pick_points: '-10',
      wrong_5_picks_streak_enabled: 'false',
      wrong_5_picks_streak_penalty: '-10',
      wrong_10_picks_streak_enabled: 'false',
      wrong_10_picks_streak_penalty: '-25',
      correct_5_picks_streak_enabled: 'false',
      correct_5_picks_streak_bonus: '10',
      correct_10_picks_streak_enabled: 'false',
      correct_10_picks_streak_bonus: '25',
      total_runs_exact_prediction_enabled: 'false',
      total_runs_exact_prediction_points: '50',
      total_runs_close_prediction_enabled: 'false',
      total_runs_close_prediction_points: '20',
      total_runs_medium_prediction_enabled: 'false',
      total_runs_medium_prediction_points: '10',
      man_of_match_prediction_enabled: 'false',
      man_of_match_prediction_points: '50',
      win_margin_prediction_enabled: 'false',
      win_margin_prediction_points: '25'
    };
    setSettings(defaultSettings);
  };

  // Initialize with proper defaults if settings are null
  useEffect(() => {
    if (settings === null) {
      resetToDefaults();
    }
  }, []);

  const handleDeleteLeague = async () => {
    if (!isManager) {
      setError('Only league managers can delete the league');
      return;
    }
    
    try {
      await apiService.deleteFantasyLeague(leagueId);
      setSuccess('League deleted successfully');
      setTimeout(() => {
        onLeagueDeleted?.();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete league');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center p-8">
        <p className="text-red-600">Failed to load league settings</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">League Settings</h2>
        {isManager && (
          <div className="flex gap-2">
            <button
              onClick={resetToDefaults}
                              disabled={leagueHasStarted}
              className="px-4 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Reset to Defaults
            </button>
            <button
              onClick={handleRecalculatePoints}
              disabled={recalculating}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {recalculating ? 'Recalculating...' : 'Recalculate Points'}
            </button>
            <button
              onClick={handleSave}
                              disabled={saving || leagueHasStarted}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {isManager && dynamicScoringEnabled !== undefined && (
        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <h3 className="text-md font-semibold text-indigo-900 mb-2">Dynamic scoring</h3>
          <p className="text-sm text-indigo-800 mb-3">
            When enabled, winner points follow the tournament points table (rank-based after every team has played once;
            playoffs +10/−5; final +15/−10). Admins maintain the table under Tournament → Points table.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={dynamicOn}
              disabled={savingDynamic}
              onChange={async (e) => {
                const v = e.target.checked;
                setDynamicOn(v);
                try {
                  setSavingDynamic(true);
                  setError(null);
                  await apiService.updatePickemDynamicScoring(leagueId, v);
                  onDynamicScoringChange?.();
                  setSuccess('Dynamic scoring updated.');
                } catch (err: any) {
                  setDynamicOn(!v);
                  setError(err.message || 'Failed to update dynamic scoring');
                } finally {
                  setSavingDynamic(false);
                }
              }}
              className="rounded border-gray-300"
            />
            <span className="text-sm font-medium text-indigo-900">
              {savingDynamic ? 'Saving…' : 'Enable dynamic scoring for this league'}
            </span>
          </label>
        </div>
      )}

                  {leagueHasStarted && isManager && (
        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center">
            <span className="text-orange-600 mr-2">⚠️</span>
            <div>
              <h4 className="text-orange-800 font-semibold">League Has Started</h4>
              <p className="text-orange-700 text-sm">
                Most scoring settings cannot be changed after the first match has started. Match-start summary email
                (below) can still be turned on or off by the league creator.
              </p>
            </div>
          </div>
        </div>
      )}

      {isManager && pickemAllPicksAlwaysVisible !== undefined && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="text-md font-semibold text-amber-950 mb-2">All Picks visibility</h3>
          <p className="text-sm text-amber-900 mb-3">
            Show everyone&apos;s picks for all matches (including before lock) on the All Picks tab. Off by default:
            picks stay hidden until each match reaches lock/start, matching the usual Pick&apos;em experience.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allPicksVisibleOn}
              disabled={savingAllPicksVisible}
              onChange={async (e) => {
                const v = e.target.checked;
                setAllPicksVisibleOn(v);
                try {
                  setSavingAllPicksVisible(true);
                  setError(null);
                  await apiService.updatePickemAllPicksVisibility(leagueId, v);
                  onPickemAllPicksVisibilityChange?.();
                  setSuccess('All Picks visibility updated.');
                } catch (err: any) {
                  setAllPicksVisibleOn(!v);
                  setError(err.message || 'Failed to update All Picks visibility');
                } finally {
                  setSavingAllPicksVisible(false);
                }
              }}
              className="rounded border-gray-300"
            />
            <span className="text-sm font-medium text-amber-950">
              {savingAllPicksVisible ? 'Saving…' : "Show everyone's picks for all matches (including before lock)"}
            </span>
          </label>
          <p className="text-xs text-amber-800/90 mt-2">League creator only. Other managers cannot change this.</p>
        </div>
      )}

      {isManager && matchStartPickSummaryEmail !== undefined && (
        <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <h3 className="text-md font-semibold text-slate-900 mb-2">Match-start picks email</h3>
          <p className="text-sm text-slate-700 mb-3">
            When enabled, all league members receive <strong>one</strong> email per fixture—in the first five minutes
            after scheduled match start—with a table of everyone&apos;s picks (winner, runs, player of match, margin)
            for that match. Off by default. This option stays available even after the league has started.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={pickSummaryEmailOn}
              disabled={savingPickSummaryEmail}
              onChange={async (e) => {
                const v = e.target.checked;
                setPickSummaryEmailOn(v);
                try {
                  setSavingPickSummaryEmail(true);
                  setError(null);
                  await apiService.updatePickemMatchStartPickSummaryEmail(leagueId, v);
                  onMatchStartPickSummaryChange?.();
                  setSuccess('Match-start picks email setting updated.');
                } catch (err: any) {
                  setPickSummaryEmailOn(!v);
                  setError(err.message || 'Failed to update email setting');
                } finally {
                  setSavingPickSummaryEmail(false);
                }
              }}
              className="rounded border-gray-300"
            />
            <span className="text-sm font-medium text-slate-900">
              {savingPickSummaryEmail ? 'Saving…' : 'Email picks summary to all members at match start'}
            </span>
          </label>
        </div>
      )}

      <div className="space-y-8">
        {/* Core Points System - Always Enabled */}
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-800 border-b border-blue-300 pb-2 mb-4">
            🎯 Core Points System (Always Active)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">
                Correct Pick Points
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded">Default: 100</span>
                <input
                  type="number"
                  value={settings.correct_pick_points}
                  onChange={(e) => handleSettingChange('correct_pick_points', e.target.value)}
                  disabled={!isManager || leagueHasStarted}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="Your Setting"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Optional Point Systems */}
        <div className="space-y-6">
          {/* Wrong Pick Points */}
          <div className="bg-red-50 p-6 rounded-lg border border-red-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-800">❌ Wrong Pick Penalty</h3>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.wrong_pick_enabled === 'true'}
                  onChange={(e) => handleSettingChange('wrong_pick_enabled', e.target.checked ? 'true' : 'false')}
                  disabled={!isManager || leagueHasStarted}
                  className="mr-2 w-4 h-4 text-red-600 bg-white/10 border-red-300 rounded focus:ring-red-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-red-700">Enable</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-red-700 mb-1">
                  Wrong Pick Points
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-red-600 bg-red-100 px-2 py-1 rounded">Default: -10</span>
                  <input
                    type="number"
                    value={settings.wrong_pick_points}
                    onChange={(e) => handleSettingChange('wrong_pick_points', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.wrong_pick_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Streak Penalties */}
          <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
            <h3 className="text-lg font-semibold text-orange-800 mb-4">📉 Streak Penalties</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-orange-700">Wrong 5 Picks in a Row</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.wrong_5_picks_streak_enabled === 'true'}
                      onChange={(e) => handleSettingChange('wrong_5_picks_streak_enabled', e.target.checked ? 'true' : 'false')}
                      disabled={!isManager || leagueHasStarted}
                      className="mr-2 w-4 h-4 text-orange-600 bg-white/10 border-orange-300 rounded focus:ring-orange-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-orange-700">Enable</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">Default: -10</span>
                  <input
                    type="number"
                    value={settings.wrong_5_picks_streak_penalty}
                    onChange={(e) => handleSettingChange('wrong_5_picks_streak_penalty', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.wrong_5_picks_streak_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-orange-700">Wrong 10 Picks in a Row</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.wrong_10_picks_streak_enabled === 'true'}
                      onChange={(e) => handleSettingChange('wrong_10_picks_streak_enabled', e.target.checked ? 'true' : 'false')}
                      disabled={!isManager || leagueHasStarted}
                      className="mr-2 w-4 h-4 text-orange-600 bg-white/10 border-orange-300 rounded focus:ring-orange-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-orange-700">Enable</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">Default: -25</span>
                  <input
                    type="number"
                    value={settings.wrong_10_picks_streak_penalty}
                    onChange={(e) => handleSettingChange('wrong_10_picks_streak_penalty', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.wrong_10_picks_streak_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Streak Bonuses */}
          <div className="bg-green-50 p-6 rounded-lg border border-green-200">
            <h3 className="text-lg font-semibold text-green-800 mb-4">📈 Streak Bonuses</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-green-700">Correct 5 Picks in a Row</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.correct_5_picks_streak_enabled === 'true'}
                      onChange={(e) => handleSettingChange('correct_5_picks_streak_enabled', e.target.checked ? 'true' : 'false')}
                      disabled={!isManager || leagueHasStarted}
                      className="mr-2 w-4 h-4 text-green-600 bg-white/10 border-green-300 rounded focus:ring-green-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-green-700">Enable</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">Default: 10</span>
                  <input
                    type="number"
                    value={settings.correct_5_picks_streak_bonus}
                    onChange={(e) => handleSettingChange('correct_5_picks_streak_bonus', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.correct_5_picks_streak_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-green-700">Correct 10 Picks in a Row</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.correct_10_picks_streak_enabled === 'true'}
                      onChange={(e) => handleSettingChange('correct_10_picks_streak_enabled', e.target.checked ? 'true' : 'false')}
                      disabled={!isManager || leagueHasStarted}
                      className="mr-2 w-4 h-4 text-green-600 bg-white/10 border-green-300 rounded focus:ring-green-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-green-700">Enable</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">Default: 25</span>
                  <input
                    type="number"
                    value={settings.correct_10_picks_streak_bonus}
                    onChange={(e) => handleSettingChange('correct_10_picks_streak_bonus', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.correct_10_picks_streak_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Total Runs Prediction */}
          <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
            <h3 className="text-lg font-semibold text-purple-800 mb-4">🏏 Total Runs Prediction</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-purple-700">Exact Prediction</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.total_runs_exact_prediction_enabled === 'true'}
                      onChange={(e) => handleSettingChange('total_runs_exact_prediction_enabled', e.target.checked ? 'true' : 'false')}
                      disabled={!isManager || leagueHasStarted}
                      className="mr-2 w-4 h-4 text-purple-600 bg-white/10 border-purple-300 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-purple-700">Enable</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded">Default: 50</span>
                  <input
                    type="number"
                    value={settings.total_runs_exact_prediction_points}
                    onChange={(e) => handleSettingChange('total_runs_exact_prediction_points', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.total_runs_exact_prediction_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-purple-700">Close Prediction (±5 runs)</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.total_runs_close_prediction_enabled === 'true'}
                      onChange={(e) => handleSettingChange('total_runs_close_prediction_enabled', e.target.checked ? 'true' : 'false')}
                      disabled={!isManager || leagueHasStarted}
                      className="mr-2 w-4 h-4 text-purple-600 bg-white/10 border-purple-300 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-purple-700">Enable</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded">Default: 20</span>
                  <input
                    type="number"
                    value={settings.total_runs_close_prediction_points}
                    onChange={(e) => handleSettingChange('total_runs_close_prediction_points', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.total_runs_close_prediction_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-purple-700">Medium Prediction (±10 runs)</label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.total_runs_medium_prediction_enabled === 'true'}
                      onChange={(e) => handleSettingChange('total_runs_medium_prediction_enabled', e.target.checked ? 'true' : 'false')}
                      disabled={!isManager || leagueHasStarted}
                      className="mr-2 w-4 h-4 text-purple-600 bg-white/10 border-purple-300 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-purple-700">Enable</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded">Default: 10</span>
                  <input
                    type="number"
                    value={settings.total_runs_medium_prediction_points}
                    onChange={(e) => handleSettingChange('total_runs_medium_prediction_points', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.total_runs_medium_prediction_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Man of the Match Prediction */}
          <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-indigo-800">🏆 Man of the Match Prediction</h3>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.man_of_match_prediction_enabled === 'true'}
                  onChange={(e) => handleSettingChange('man_of_match_prediction_enabled', e.target.checked ? 'true' : 'false')}
                  disabled={!isManager || leagueHasStarted}
                  className="mr-2 w-4 h-4 text-indigo-600 bg-white/10 border-indigo-300 rounded focus:ring-indigo-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-indigo-700">Enable</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-indigo-700 mb-1">
                  Correct Prediction Points
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-indigo-600 bg-indigo-100 px-2 py-1 rounded">Default: 50</span>
                  <input
                    type="number"
                    value={settings.man_of_match_prediction_points}
                    onChange={(e) => handleSettingChange('man_of_match_prediction_points', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.man_of_match_prediction_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Win Margin Prediction */}
          <div className="bg-teal-50 p-6 rounded-lg border border-teal-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-teal-800">🎯 Win Margin Prediction</h3>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.win_margin_prediction_enabled === 'true'}
                  onChange={(e) => handleSettingChange('win_margin_prediction_enabled', e.target.checked ? 'true' : 'false')}
                  disabled={!isManager || leagueHasStarted}
                  className="mr-2 w-4 h-4 text-teal-600 bg-white/10 border-teal-300 rounded focus:ring-teal-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-teal-700">Enable</span>
              </label>
            </div>
            <div className="mb-4">
              <p className="text-sm text-teal-700 mb-2">
                Users predict if the match will be won with an Easy, Medium, or Hard margin:
              </p>
              <ul className="text-xs text-teal-600 space-y-1 ml-4">
                <li>• <strong>Easy:</strong> Win by 25+ runs (batting first) or 25+ balls remaining (chasing)</li>
                <li>• <strong>Medium:</strong> Win by 10-24 runs or balls</li>
                <li>• <strong>Hard:</strong> Win by less than 10 runs or balls</li>
              </ul>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-teal-700 mb-1">
                  Correct Prediction Points
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-teal-600 bg-teal-100 px-2 py-1 rounded">Default: 25</span>
                  <input
                    type="number"
                    value={settings.win_margin_prediction_points}
                    onChange={(e) => handleSettingChange('win_margin_prediction_points', e.target.value)}
                    disabled={!isManager || leagueHasStarted || settings.win_margin_prediction_enabled !== 'true'}
                    className="flex-1 px-3 py-2 border border-teal-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                    placeholder="Your Setting"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!isManager && (
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-800 text-sm">
              Only league managers can modify these settings. Contact your league manager if you need changes.
            </p>
          </div>
          
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <h4 className="text-orange-800 font-semibold mb-2">League Actions</h4>
            <p className="text-orange-700 text-sm mb-3">
              You can leave this league at any time.
            </p>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to leave this league? You will lose access to all your predictions and standings.')) {
                  onLeaveLeague?.();
                }
              }}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors text-sm"
            >
              Leave League
            </button>
          </div>
        </div>
      )}

      {/* Manager Tools */}
      {isManager && (
        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            Manager Tools
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-red-800 font-semibold mb-2">Danger Zone</h4>
              <p className="text-red-700 text-sm mb-3">
                These actions cannot be undone. Please be careful.
              </p>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this league? This action cannot be undone.')) {
                    handleDeleteLeague();
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
              >
                Delete League
              </button>
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="text-yellow-800 font-semibold mb-2">User Management</h4>
              <p className="text-yellow-700 text-sm mb-3">
                Manage league participants and permissions.
              </p>
              <button
                onClick={() => setShowUserManagement(true)}
                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors text-sm"
              >
                Manage Users
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      <UserManagementModal
        leagueId={leagueId}
        isOpen={showUserManagement}
        onClose={() => setShowUserManagement(false)}
        onUserRemoved={onSettingsUpdated || (() => {})}
      />
    </div>
  );
};

export default LeagueSettings; 
